import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {Store} from '@ngrx/store';
import {ShippingDetailFormComponent} from './components/shipping-detail-form/shipping-detail-form.component';
import {ConsignmentFormComponent} from './components/consignment-form/consignment-form.component';
import {InitInformation} from '@models/init-information.interface';
import {ShipmentService} from '@services/shipment.service';
import {ActivatedRoute, Router} from '@angular/router';
import {CreateConsignmentService} from '../create-consignment.service';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs/Subject';
import {AppState, SearchResult, selectFooterAction, selectUser} from '@store/reducers';
import {UUID} from 'angular2-uuid';
import {AccountService} from '@services/account.service';
import {Package} from '@models/package.interface';
import {ClearOrderCompletionState, ClearOrderCreationState, UpdateSavedShipment} from '@store/actions';
import {SavedShipment} from '@models/saved-shipment.interface';
import {FooterActionEnum} from '@models/footer-action.enum';
import {ConsignmentSections} from '@models/consignment-section.enum';
import {User} from '@models/user.interface';
import {SnackBarComponent} from '@components/snackbar/snackbar.component';
import {MatSnackBar} from '@angular/material';
import {TranslateService} from '@ngx-translate/core';
import 'rxjs/add/operator/map';
import {CustomsService} from '@services/customs.service';
import {Subscription} from 'rxjs/Subscription';
import {CustomsFormComponent} from '@modules/consignment/search/components/customs-form/customs-form.component';
import {Observable} from 'rxjs/Observable';
import {Observer} from 'rxjs/Observer';
// import {ProductsFormComponent} from '@modules/consignment/search/components/products-form/products-form.component';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss']
})
export class SearchComponent implements OnInit, OnDestroy {
  public showCustomsForm = false;
  private isDutiableSubscription$: Subject<boolean> = new Subject();
  private unsubscribe: Subject<void> = new Subject();
  form: FormGroup;
  submitted: boolean;
  currentSearchResult: SearchResult;
  packages: Package[] = [];
  shippingDetails: {parcel_value: string, parcel_content: string, daily_pickup: boolean, pickup_date: string, pickup_time: string};
  currentSearchIdSaved: number | null;

  @Output() changeCurrentSection: EventEmitter<ConsignmentSections> = new EventEmitter();
  @Input() parent: FormGroup;

  countryRules: {
    from: Array<{value: string; label: string}>,
    to: Array<{value: string; label: string}>
  };

  constructor(
    private shipmentService: ShipmentService,
    private route: ActivatedRoute,
    private store: Store<AppState>,
    private router: Router,
    private accountService: AccountService,
    private consignmentService: CreateConsignmentService,
    private matSnackBar: MatSnackBar,
    private translate: TranslateService,
    private customsService: CustomsService,
  ) {}

  ngOnInit() {

    this.countryRules = this.shipmentService.getCountryRules();
    if (!this.countryRules) {
      this.shipmentService.getInitialData()
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((data: InitInformation) => {
          this.countryRules = this.shipmentService.getCountryRules();
        });
    }

    this.form = new FormGroup({
      from: ConsignmentFormComponent.generateForm(),
      to: ConsignmentFormComponent.generateForm(),
      pickup: ConsignmentFormComponent.generateForm(),
      delivery: ConsignmentFormComponent.generateForm(),
      customs_data: CustomsFormComponent.generateForm(),
      reference: new FormControl(''),
      product_id: new FormControl(''),
      filters: new FormControl(''),
      referring_consignment_number: new FormControl(null),
      // products: ProductsFormComponent.generateForm()
    });

    this.setIsDutiableOnCountryChange();

    this.setCustomsFormBasedOnIsDutiable();

    this.store.select(selectFooterAction)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(action => {
        switch (action) {
          case FooterActionEnum.SAVE_SHIPMENT:
            this.saveShipment();
            break;
          case FooterActionEnum.GO_TO_CREATE_CONSIGNMENT_SEARCH_RESULT:
            // TODO: when saving correct search_id is not passed
            if (this.currentSearchIdSaved) {
              this.saveShipment(false);
            }
            this.handleSearch();
            break;
        }
      });

    this.store.select('orderCreation')
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(data => {
        // console.log(data);
        if (data.activeSearchId) {
          // Edit consignment
          this.currentSearchResult = data.searchResults.find(({id, search_id}) => {
            return (search_id && search_id.toString() === data.activeSearchId) || (id === data.activeSearchId);
          });
          this.currentSearchIdSaved = this.currentSearchResult.search_id;
          this.setSearchData(this.currentSearchResult.params);
          // this.saveShipment(false);
        } else {
          // Create consignment
          this.currentSearchResult = null;
          this.currentSearchIdSaved = null;
          this.resetSearchData();
          // this.store.dispatch(new ClearOrderCreationState()); // This causes browser to hang on create consignment page.
        }
      });
  }

  /**
   * Set customs form based on is_dutiable
   * @private
   */
  private setCustomsFormBasedOnIsDutiable() {
    this.customsService.getIsDutiable()
      .pipe(takeUntil(this.isDutiableSubscription$))
      .subscribe(isDutiable => {
        this.showCustomsForm = isDutiable;
        if (isDutiable) {
          if (!this.form.contains('customs_data')) {
            this.form.addControl('customs_data', CustomsFormComponent.generateForm());
          }
        } else {
          if (this.form.contains('customs_data')) {
            this.form.removeControl('customs_data');
          }
        }
      });
  }

  /**
   * Set is_dutiable on country change
   * @private
   */
  private setIsDutiableOnCountryChange() {
    let fromCountryCode;
    let toCountryCode;
    this.form.valueChanges.subscribe(event => {
      if (event.pickup.is_used === true) {
        fromCountryCode = event.pickup.country_code;
      } else {
        fromCountryCode = event.from.country_code;
      }

      if (event.delivery.is_used === true) {
        toCountryCode = event.delivery.country_code;
      } else {
        toCountryCode = event.to.country_code;
      }

      this.customsService.setIsDutiable(fromCountryCode, toCountryCode);
    });
  }

  /**
   * Set values to consignment form
   * @param data
   */
  setSearchData(data) {
    // Never set save address checkbox
    delete data.from.save_address;
    delete data.to.save_address;
    const {reference, packages, parcel_value, parcel_content, daily_pickup, pickup_date, pickup_time, product_id, filters, referring_consignment_number, ...rest} = data;
    this.packages = data.packages.map(pack => {
      const {length, width, height, weight, ...rest} = pack;
      return {
        ...rest,
        length: Math.round(length * 100),
        width: Math.round(width * 100),
        height: Math.round(height * 100),
        weight: Math.round(weight)
      };
    });

    this.form.get('reference').setValue(data.reference);
    this.form.get('product_id').setValue(data.product_id);
    this.form.get('filters').setValue(data.filters);
    this.form.get('referring_consignment_number').setValue(data.referring_consignment_number);
    this.shippingDetails = {parcel_value, parcel_content, daily_pickup, pickup_date, pickup_time};

    this.form.get('from').patchValue(rest.from);
    this.form.get('to').patchValue(rest.to);

    if (rest.pickup) {
      this.form.get('pickup').patchValue(rest.pickup);
    }

    if (rest.delivery) {
      this.form.get('delivery').patchValue(rest.delivery);
    }

    if (rest.customs_data) {
      this.form.get('customs_data').patchValue(rest.customs_data);
    }
  }

  resetSearchData() {
    this.form.get('from').reset({country_code: 'SE', country: 'Sweden'}, {emitEvent: false});
    this.form.get('to').reset({country_code: 'SE', country: 'Sweden'}, {emitEvent: false});
    this.form.get('pickup').reset({country_code: 'SE', country: 'Sweden'}, {emitEvent: false});
    this.form.get('delivery').reset({country_code: 'SE', country: 'Sweden'}, {emitEvent: false});
    const date_time = ShippingDetailFormComponent.validDateTime;
    if (!this.form.get('pickupDateTime')) {
      return;
    }
    this.form.get('pickupDateTime.date').setValue(date_time.date);
    this.form.get('pickupDateTime.time').setValue(date_time.time);
    this.form.get('parcel_value').setValue('');
    this.form.get('parcel_content').setValue('');
  }

  saveShipment(redirect: boolean = true) {
    if (this.isFormInvalid()) {
      this.submitted = true;
      return;
    }

    this.consignmentService.saveAddress(this.form);

    const data: SavedShipment = {
      from: this.form.get('from').value,
      to: this.form.get('to').value,
      reference: this.form.get('reference').value,
      packages: this.form.getRawValue().packages.map(pack => {
        const {length, width, height, ...rest} = pack;
        return {
          ...rest,
          length: length / 100,
          width: width / 100,
          height: height / 100
        };
      }),
      parcel_value: this.form.get('parcel_value').value,
      parcel_content: this.form.get('parcel_content').value,
      daily_pickup: this.form.get('daily_pickup').value,
      pickup_date: this.form.get('pickupDateTime.date').value,
      pickup_time: this.form.get('pickupDateTime.time').value,
      title: UUID.UUID(),
      product_id: this.form.get('product_id').value,
      filters: this.form.get('filters').value,
      referring_consignment_number: this.form.get('referring_consignment_number').value,
    };

    if (this.currentSearchResult && this.currentSearchResult.search_id) {
      data.search_id = this.currentSearchResult.search_id;
    }

    if (this.form.get('pickup.is_used').value) {
      data.pickup = this.form.get('pickup').value;
    }

    if (this.form.get('delivery.is_used').value) {
      data.delivery = this.form.get('delivery').value;
    }

    if (this.form.contains('customs_data')) {
      data.customs_data = this.form.get('customs_data').value;
    }

    this.store.dispatch(new UpdateSavedShipment(data));

    if (redirect) {
      this.router.navigate(['shipments/saved']);
    }
  }

  handleSearch() {
    if (this.isFormInvalid()) {
      this.submitted = true;
      return;
    }
    /*
    if (this.form.get('pickup.is_used').value === false) {
      this.form.removeControl('pickup');
      this.form.updateValueAndValidity();
    }
    if (this.form.get('delivery.is_used').value === false) {
      this.form.removeControl('delivery');
      this.form.updateValueAndValidity();
    }
    */

    this.consignmentService.saveAddress(this.form);
    this.router.navigate(['create-consignment', 'search-result']);
    const searchResultId = this.currentSearchResult && this.currentSearchResult.id;
    this.consignmentService.saveSearchParamsToState(this.form.getRawValue(), searchResultId);
  }

  isFormInvalid() {
    if (this.form.get('from').invalid ||
      this.form.get('to').invalid ||
      (this.form.get('pickup').get('is_used').value === true && this.form.get('pickup').invalid) ||
      (this.form.get('delivery').get('is_used').value === true && this.form.get('delivery').invalid) ||
      this.form.get('packages').invalid ||
      this.form.get('parcel_value').invalid ||
      this.form.get('parcel_content').invalid ||
      (this.form.contains('customs_data') && this.form.get('customs_data').invalid) ||
      this.form.get('pickupDateTime').invalid) {

      this.matSnackBar.openFromComponent(SnackBarComponent, {
        data: {
          error: true,
          message: this.translate.instant('error.validation.general')
        },
        panelClass: 'snackbar-container',
        duration: 3000
      });

      return true;
    }
  }

  limitCountries({fromCountry, toCountry}) {
    this.countryRules = this.shipmentService.getCountryRules(fromCountry, toCountry);
  }

  ngOnDestroy() {
      this.unsubscribe.next();
      this.unsubscribe.complete();
      this.isDutiableSubscription$.next();
      this.isDutiableSubscription$.unsubscribe();
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/consignment/search/search.component.ts